import React, { useState } from 'react';
import { Tab, Tabs } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import _ from 'lodash';

import * as actions from '../../../state';
import styles from './styles.module.css';
import StepHeader from './StepHeader';
import StepFooter from './StepFooter';
import ChartTypeSelect from './chart-type-select/ChartTypeSelect';
import DataSources from './data-source-controls/DataSources';
import StyleControls from './style-controls/StyleControls';
import { TYPE_TAB, DATA_TAB, STYLE_TAB } from './tabsConstants';
import createChartNameFromSources from '../../../utils/createChartNameFromSources';

const messages = defineMessages({
  TYPE: {
    id: 'TYPE',
    defaultMessage: 'Type',
  },
  DATA: {
    id: 'DATA',
    defaultMessage: 'Data',
  },
  STYLE: {
    id: 'STYLE',
    defaultMessage: 'Style',
  },
  DONE: {
    id: 'DONE',
    defaultMessage: 'Done',
  },
});

const maxTabValue = 2; // tab amount

function ChartControlSheet() {
  const dispatch = useDispatch();

  const chartsManifest = useSelector(actions.readChartsManifest);
  const originalCharts = useSelector((state) => state.editHistory.originalCharts);
  const selectedChart = useSelector(actions.readStateSelectedChart);

  /* default to data tab */
  let defaultSelectedTab = DATA_TAB;

  /* default to type select if card is new */
  if (selectedChart.new) { defaultSelectedTab = TYPE_TAB; }

  /* default to style tab if editing header */
  if (selectedChart.type === 'Header') { defaultSelectedTab = STYLE_TAB; }

  const [selectedTab, setSelectedTab] = useState(defaultSelectedTab);
  const [, setSelectedSource] = useState(defaultSelectedTab);
  const [tabContent, setTabContent] = useState('base');

  /*
   * used to track tab content layers. some controls have multiple input
   * steps, which are managed using tabContent. 'base' is the initial
   * content of the tab
   */
  const [alertConfig, setAlertConfig] = useState({
    sourceIndex: 0,
    direction: null,
    color: '#ff0000',
    error: false,
  });

  /* keep a reference of the original source configuration to use for undoing changes */
  const [origSource, setOrigSource] = useState({});

  /* keep a reference of the original alert configuration to use for undoing changes */
  const [origAlert, setOrigAlert] = useState({});

  /* keep a reference of the original column selection to use for backing out of column toggle */
  const [origColumns, setOrigColumns] = useState({});

  const handleTabContent = (tab) => {
    setTabContent(tab);

    switch (tab) {
      case 'configureSource':
        //
        break;
      case 'selectStat':
        //
        break;
      case 'toggleColumns':
        setSelectedSource(selectedChart.data_sources[0]);
        break;
      case 'configureAlert':
        break;
      default:
    }
  };

  const saveOrigSource = (source) => {
    setOrigSource(_.cloneDeep(source));
  };

  const saveOrigAlert = (alert) => {
    setOrigAlert(_.cloneDeep(alert));
  };

  const saveOrigColumns = (columns) => {
    setOrigColumns(_.cloneDeep(columns));
  };

  const handleTabSelect = (event, tab) => {
    setSelectedTab(tab);
    setTabContent('base');
  };

  /* clicking 'back' or the back navigation fires this logic. any sidesheet
   * back navigation behaviors are handled here */
  const backAction = () => {
    const clonedChart = _.cloneDeep(selectedChart);

    const sources = clonedChart.data_sources;

    switch (tabContent) {
      case 'configureSource':
        if (origSource.index !== undefined && origSource.index !== null) { // if editing an existing source, re-apply origSource
          // eslint-disable-next-line no-undef
          clonedChart.data_sources[origSource.index] = _.cloneDeep(origSource);
          setOrigSource({});
          setTabContent('base');
        } else { // if backing out of a new source, delete the placeholder
          const newSourceIndex = sources.length - 1;
          clonedChart.data_sources.splice(newSourceIndex, 1);
          setTabContent('selectStat');
        }

        clonedChart.renderRequired = true;
        clonedChart.name = createChartNameFromSources(chartsManifest, clonedChart);
        dispatch(actions.updateSelectedChart(clonedChart));
        break;
      case 'selectStat':
        setTabContent('base');
        break;
      case 'toggleColumns':
        clonedChart.data_sources[0].queryParams.stats = origColumns;
        dispatch(actions.updateSelectedChart(clonedChart));
        setTabContent('base');
        break;
      case 'configureAlert':
        const origSourceIndex = origAlert.sourceIndex; // eslint-disable-line no-case-declarations
        const origAlertIndex = origAlert.index; // eslint-disable-line no-case-declarations

        if (typeof origSourceIndex !== 'undefined' && origSourceIndex !== null) { // if editing an existing source, re-apply origSource
          clonedChart.data_sources[origSourceIndex].alerts[origAlertIndex] = _.cloneDeep(origAlert);
          setOrigAlert({});
          setTabContent('base');
        } else { // if backing out of a new source, delete the placeholder
          if (sources.alerts && sources.alerts.length) {
            const newAlertIndex = sources.alerts.length - 1;
            clonedChart.data_sources.alerts.splice(newAlertIndex, 1);
          }
          setTabContent('selectStat');
        }
        clonedChart.renderRequired = true;
        dispatch(actions.updateSelectedChart(clonedChart));
        setTabContent('base');
        break;
      default: // go to previous tab
        setSelectedTab(selectedTab === TYPE_TAB ? TYPE_TAB : selectedTab - 1);
        break;
    }
  };

  /* clicking 'next' or actions that need a deeper layer of content will fire
   * this logic. any sidesheet forward navigation behaviors are handled here */
  const nextAction = () => {
    switch (tabContent) {
      case 'selectStat':
        setTabContent('configureSource');
        break;
      case 'configureSource':
        setTabContent('base');
        break;
      case 'configureAlert':
        setTabContent('base');
        break;
      case 'toggleColumns':
        setTabContent('base');
        break;
      default: { // go to next tab
        const nextTab = selectedTab >= maxTabValue
          ? maxTabValue
          : selectedTab + 1;
        setSelectedTab(nextTab);
        setTabContent('base');
        break;
      }
    }
  };

  const saveChart = () => {
    dispatch(actions.isDirty(true));
    dispatch(actions.selectedChartId(false));
    dispatch(actions.showRightSidebar(false));
  };

  const selectAlert = (alert) => {
    setAlertConfig(alert);
  };

  // eslint-disable-next-line consistent-return
  const saveAlert = () => {
    const sourceIndex = selectedChart.type === 'ResponsiveTable' // placeholder, replace with chartdef.usesColumns
      ? 0
      : alertConfig.sourceIndex;

    if (!alertConfig.threshold) {
      alertConfig.error = 'threshold';
      setAlertConfig(alertConfig);
      dispatch(actions.updateSelectedChart(selectedChart));

      return false;
    }
    alertConfig.error = false;
    setAlertConfig(alertConfig);

    const sourceAlerts = selectedChart.data_sources[sourceIndex].alerts || [];

    if (typeof alertConfig.index !== 'undefined' && alertConfig.index !== null) {
      sourceAlerts[alertConfig.index] = alertConfig;
    } else {
      sourceAlerts.push(alertConfig);
    }

    selectedChart.data_sources[sourceIndex].alerts = sourceAlerts;
    selectedChart.renderRequired = true;

    setTabContent('base');
    dispatch(actions.updateSelectedChart(selectedChart));
    dispatch(actions.isDirty(true));
  };

  const cancelChart = () => {
    const origChart = originalCharts.find((chart) => chart.id === selectedChart.id);

    if (origChart) {
      origChart.renderRequired = true;
      dispatch(actions.updateSelectedChart(origChart));
    }

    dispatch(actions.selectedChartId(false));
    dispatch(actions.showRightSidebar(false));
  };

  const selectedChartType = selectedChart.type;

  // eslint-disable-next-line max-len
  const showTabs = selectedChartType !== 'Header' && !['selectStat', 'configureSource', 'configureAlert', 'toggleColumns'].includes(tabContent);

  return (
    <div className={styles.list}>
      <StepHeader
        tabContent={tabContent}
        selectedTab={selectedTab}
        backAction={backAction}
        cancelChart={cancelChart}
      />

      { showTabs && (
        <Tabs
          id="sidesheet_tabs"
          textColor="primary"
          indicatorColor="primary"
          value={selectedTab}
          onChange={handleTabSelect}
          classes={{ root: styles.nsTabs }}
        >
          <Tab
            label={<FormattedMessage {...messages.TYPE} />}
            disabled={!selectedChart.new}
          />

          <Tab
            label={<FormattedMessage {...messages.DATA} />}
            disabled={selectedChartType === 'new'}
          />

          <Tab
            label={<FormattedMessage {...messages.STYLE} />}
            disabled={selectedChartType === 'new'}
          />
        </Tabs>
      )}

      { selectedTab === TYPE_TAB
        && <ChartTypeSelect />}

      { selectedTab === DATA_TAB && (
        <DataSources
          tabContent={tabContent}
          selectedChartType={selectedChartType}
          saveOrigSource={saveOrigSource}
          saveOrigAlert={saveOrigAlert}
          saveOrigColumns={saveOrigColumns}
          alertConfig={alertConfig}
          selectAlert={selectAlert}
          setTabContent={handleTabContent}
        />
      )}

      { selectedTab === STYLE_TAB && selectedChart
        && <StyleControls />}

      <StepFooter
        tabContent={tabContent}
        selectedTab={selectedTab}
        saveChart={saveChart}
        saveAlert={saveAlert}
        backAction={backAction}
        nextAction={nextAction}
        origSource={origSource}
        origAlert={origAlert}
      />
    </div>
  );
}

export default ChartControlSheet;
